<template>
    <div
        class="z-select"
        v-if="options.length"
        :class="[
            `z-select--size-${size}`,
            {
                'z-select--filter': isFilter,
                'z-select--clearable': isClearable,
                'is-opened': isOpened,
                'is-errored': isValid === false,
                'is-valid': isValid && !isFilter,
                'is-filled': selected && selected.length,
                'is-required': required,
                'z-select--multi': multi,
                'is-disabled': disabled,
            }
        ]"
        v-click-outside="onClickOutside"
    >
        <p
            class="z-select__title u-mb-2xs"
            v-if="title"
            v-html="title"
        ></p>
        <div class="z-select__content">
            <div class="z-select__wrapper" @click="setOpenState(!isOpened)" tabindex="0">
                <span
                    v-if="placeholder"
                    v-html="placeholder"
                    class="z-select__placeholder"
                ></span>
                <div class="z-select__container">
                    <div class="z-select__selected">
                        <span
                            class="z-select__value"
                            v-html="getValueText(selected)"
                            v-if="!multi"
                        ></span>
                        <template v-else>
                            <span
                                class="z-select__value"
                                v-for="(item, index) in selected"

                                :key="index"
                            >
                                <span v-html="getValueText(item)"></span>
                                <span
                                    @click.stop="removeSelectedOption(item)"
                                    class="z-select__value-delete"
                                ></span>
                            </span>
                        </template>
                        <!-- <input
                            class="z-select__input"
                            type="text"
                            disabled
                            :value="valueText"
                            autocomplete="off"
                        > -->
                    </div>
                    <div class="z-select__right">
                        <span class="z-select__count" v-if="multi && selected.length">
                            {{ selected.length }}
                        </span>
                        <span
                            v-if="isClearable && selected && selected.length"
                            class="z-select__clear"
                            @click.stop="clear"
                        ></span>
                        <span class="z-select__arrow">
                            <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M3.81121 6.29154C3.51832 5.99865 3.04345 5.99865 2.75055 6.29154C2.45766 6.58444 2.45766 7.05931 2.75055 7.3522L9.46968 14.0713C9.61034 14.212 9.8011 14.291 10 14.291C10.1989 14.291 10.3897 14.212 10.5303 14.0713L17.2495 7.3522C17.5424 7.05931 17.5424 6.58444 17.2495 6.29154C16.9566 5.99865 16.4817 5.99865 16.1888 6.29154L10 12.4804L3.81121 6.29154Z" fill="#656565"/>
                            </svg>
                        </span>
                    </div>
                </div>
            </div>
            <div class="z-select__dropdown" v-if="isOpened">
                <div
                    class="z-select__options"
                    v-for="(group, groupIndex) in groupedOptions"
                    :key="groupIndex"
                >
                    <span
                        class="z-select__options-caption"
                        v-html="group.name"
                        v-if="group.name"
                    ></span>
                    <ul
                        v-if="group.items && group.items.length"
                        class="z-select__options-group"
                    >
                        <li
                            class="z-select__option"
                            v-for="option in group.items"
                            :class="{
                                'is-selected': isOptionSelected(option.id),
                                'is-disabled': option.disabled
                            }"
                            @click="onSelectOption(option.id)"
                            :key="option.id"
                        >
                            <vue-raw :raw="option.text" />
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <span
            class="z-select__error"
            v-if="error && isValid === false"
            v-html="error"
        ></span>

    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeMount, ref } from 'vue'
import {localize} from '@/utils/i18n'

const props = defineProps({
    modelValue: [String, Array, Number],
    options: {
        type: Array,
        default: () => []
    },
    placeholder: String,
    title: String,
    clearable: Boolean,
    isFilter: Boolean,
    required: Boolean,
    multi: Boolean,
    disabled: {
        type: Boolean,
        default: false
    },
    size: {
        type: String,
        default: 'l',
        validator: prop => ['l', 'm', 's'].includes(prop)
    }
})

const emit = defineEmits([
    'update:modelValue',
    'change',
    'input'
])

// static data
const errorText = localize({
    ru: 'Поле обязательно для заполнения',
    en: 'Required field',
    cn: '必填项'
})

// computed
const isClearable = computed(() => props.isFilter || props.clearable)
const selected = computed({
    get() {
        if (props.modelValue !== undefined) {
            localValue.value = props.modelValue
            return props.modelValue
        }
        return localValue.value
    },
    set(value) {
        console.log('set');
        localValue.value = value
        emit('update:modelValue', value)
    }
})
const groupedOptions = computed(() => {
    let result = {}
    props.options.forEach(item => {
        if (item.group) {
            if (!result[item.group]) result[item.group] = {}
            if (!result[item.group].items) result[item.group].items = []
            result[item.group].name = item.group
            result[item.group].items.push(item)
        } else {
            if (!result.nogroup) result.nogroup = {}
            if (!result.nogroup.items) result.nogroup.items = []
            result.nogroup.name = ''
            result.nogroup.items.push(item)
        }
    })

    return result
})

// ref
let isOpened = ref(false)
let localValue = ref('')
let isValid = ref(null)
let error = ref('')

// methods
const clear = () => {
    emit('update:modelValue', '')
    emit('change', selected.value)
    localValue.value = ''
    isOpened.value = false
    nextTick(validate)
}
const setOpenState = state => isOpened.value = state
const onSelectOption = id => {
    if (!props.multi) {
        // selected.value = id
        localValue.value = id
        emit('update:modelValue', id)
        isOpened.value = false
    } else {
        if (!localValue.value) localValue.value = []
        localValue.value.includes(id) ? removeSelectedOption(id) : localValue.value.push(id)
        emit('update:modelValue', localValue.value)
    }

    emit('change', selected.value)
    nextTick(validate)
}
const onClickOutside = () => setOpenState(false)
const getValueText = id => props.options.find(item => item.id === id)?.text || ''
const isOptionSelected = id => {
    if (!props.multi) return id === selected.value
    return selected.value.includes(id)
}
const removeSelectedOption = id => {
    const index = selected.value.indexOf(id)
    localValue.value.splice(index, 1)
    emit('update:modelValue', localValue)
    emit('change', selected.value)
    nextTick(validate)
}
const validate = () => {
    if (props.required)  {
        if (!(selected.value && selected.value.length)) {
            isValid.value = false
            error.value = errorText
        } else {
            isValid.value = true
            error.value = ''
        }
    } else {
        isValid.value = null
        error.value = ''
    }
}

onBeforeMount(() => {
    if (props.modelValue) localValue.value = props.modelValue
})

defineExpose({
    validate,
    error,
    isValid
})
</script>

<style lang="scss" src="./index.scss"></style>
